import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Layers,
  AlertTriangle,
  ArrowLeft,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
} from 'lucide-react';
import { useFeedingGroups } from '../../contexts/FeedingGroupsContext';
import { usePets } from '../../contexts/PetsContext';
import { useToast } from '../../contexts/ToastContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import { ROUTES, VALIDATION, INVENTORY } from '../../config/constants';
import { formatGrams } from '../../lib/format';

/**
 * Edición de un grupo existente.
 *
 * Campos editables del grupo en sí (updateFeedingGroup):
 *  - name
 *  - bagCapacityGr (bloqueado si hay reserva)
 *
 * Dispensers: CRUD inline contra los endpoints específicos
 * (addDispenser / removeDispenser / renameDispenser / resizeDispenser).
 *
 * No se editan acá: stock actual del dispenser ni de la bolsa (eso va por
 * "Ajustar stock" / "Compré bolsas" / "Abrir bolsa").
 */

const FIELD_ERROR_MAP = {
  INVALID_NAME: 'name',
  INVALID_BAG_CAPACITY: 'bagCapacityGr',
  INVALID_LOW_STOCK_THRESHOLD: 'lowStockThresholdDays',
};

const EditGroupPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const {
    getGroupById,
    updateFeedingGroup,
    softDeleteFeedingGroup,
    isLoading,
    addDispenser,
    removeDispenser,
    renameDispenser,
    resizeDispenser,
  } = useFeedingGroups();
  const { pets } = usePets();
  const toast = useToast();

  const group = getGroupById(groupId);

  const [name, setName] = useState('');
  const [bagCapacityGr, setBagCapacityGr] = useState('');
  const [lowStockThresholdDays, setLowStockThresholdDays] = useState('');
  const [errors, setErrors] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Edición inline por dispenser. dispenserId → { mode: 'name'|'size', value }.
  const [editing, setEditing] = useState({});
  const [busyDispenserId, setBusyDispenserId] = useState(null);

  // Form de "agregar dispenser" (inline, dentro de la card).
  const [addingDispenser, setAddingDispenser] = useState(false);
  const [newDispenserName, setNewDispenserName] = useState('');
  const [newDispenserCapacity, setNewDispenserCapacity] = useState('');
  const [newDispenserErrors, setNewDispenserErrors] = useState({});
  const [addingBusy, setAddingBusy] = useState(false);

  useEffect(() => {
    if (!group) return;
    setName(group.name ?? '');
    setBagCapacityGr(group.bagCapacityGr != null ? String(group.bagCapacityGr) : '');
    setLowStockThresholdDays(
      group.lowStockThresholdDays != null ? String(group.lowStockThresholdDays) : ''
    );
  }, [group]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={ArrowLeft}
          onClick={() => navigate(-1)}
          className="self-start"
        >
          Volver
        </Button>
        <Card>
          <p className="text-sm text-slate-500">Cargando...</p>
        </Card>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="flex flex-col gap-4">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={ArrowLeft}
          onClick={() => navigate(-1)}
          className="self-start"
        >
          Volver
        </Button>
        <Card>
          <p className="text-sm text-slate-600">
            No encontramos este grupo. Puede haber sido eliminado.
          </p>
        </Card>
      </div>
    );
  }

  const dispensers = group.dispensers ?? [];
  const petsInGroup = pets.filter(
    (p) => p.feedingGroupId === groupId && !p.deletedAt
  );
  const bagCapacityLocked = (group.extraBagsCount ?? 0) > 0 && group.bagCapacityGr != null;
  const canAddMore = dispensers.length < VALIDATION.MAX_DISPENSERS_PER_GROUP;

  const handleSubmitGroup = async (e) => {
    e?.preventDefault();
    setErrors({});

    const updates = {};
    if (name.trim() !== group.name) updates.name = name;
    if (
      !bagCapacityLocked &&
      bagCapacityGr !== '' &&
      Number(bagCapacityGr) !== group.bagCapacityGr
    ) {
      updates.bagCapacityGr = Number(bagCapacityGr);
    }
    // Fase 6: umbral configurable para "Comprar pronto".
    // Input vacío = null (usar default global). Si tiene número, normalizamos.
    const thresholdInput = lowStockThresholdDays.trim();
    const thresholdNum = thresholdInput === '' ? null : Number(thresholdInput);
    if (thresholdNum !== (group.lowStockThresholdDays ?? null)) {
      updates.lowStockThresholdDays = thresholdNum;
    }

    if (Object.keys(updates).length === 0) {
      toast.info('No hay cambios para guardar.');
      return;
    }

    try {
      setSubmitting(true);
      await updateFeedingGroup(groupId, updates);
      toast.success(`Grupo "${name.trim()}" actualizado.`);
      navigate(ROUTES.INVENTORY, { replace: true });
    } catch (err) {
      const field = FIELD_ERROR_MAP[err.code];
      if (field) {
        setErrors({ [field]: err.message });
      } else {
        toast.error(err.message || 'No se pudo actualizar el grupo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const startEditName = (dispenser) => {
    setEditing({ [dispenser.id]: { mode: 'name', value: dispenser.name } });
  };
  const startEditSize = (dispenser) => {
    setEditing({ [dispenser.id]: { mode: 'size', value: String(dispenser.capacityGr) } });
  };
  const cancelEdit = () => setEditing({});

  const commitEditName = async (dispenser) => {
    const state = editing[dispenser.id];
    if (!state || state.mode !== 'name') return;
    const newName = state.value.trim();
    if (!newName) {
      toast.error('El nombre no puede estar vacío.');
      return;
    }
    if (newName === dispenser.name) {
      cancelEdit();
      return;
    }
    try {
      setBusyDispenserId(dispenser.id);
      await renameDispenser(groupId, dispenser.id, newName);
      toast.success('Nombre actualizado.');
      cancelEdit();
    } catch (err) {
      toast.error(err.message || 'No se pudo renombrar el dispenser.');
    } finally {
      setBusyDispenserId(null);
    }
  };

  const commitEditSize = async (dispenser) => {
    const state = editing[dispenser.id];
    if (!state || state.mode !== 'size') return;
    const newCap = Number(state.value);
    if (!Number.isInteger(newCap) || newCap <= 0) {
      toast.error('Capacidad inválida.');
      return;
    }
    if (newCap === dispenser.capacityGr) {
      cancelEdit();
      return;
    }
    try {
      setBusyDispenserId(dispenser.id);
      await resizeDispenser(groupId, dispenser.id, newCap);
      toast.success('Capacidad actualizada.');
      cancelEdit();
    } catch (err) {
      toast.error(err.message || 'No se pudo cambiar la capacidad.');
    } finally {
      setBusyDispenserId(null);
    }
  };

  const handleRemove = async (dispenser) => {
    const stock = dispenser.stockGr ?? 0;
    const msg =
      stock > 0
        ? `¿Quitar "${dispenser.name}"? Los ${formatGrams(stock)} pasan a la bolsa principal.`
        : `¿Quitar "${dispenser.name}"?`;
    if (!window.confirm(msg)) return;

    try {
      setBusyDispenserId(dispenser.id);
      await removeDispenser(groupId, dispenser.id);
      toast.success(`${dispenser.name} eliminado.`);
    } catch (err) {
      toast.error(err.message || 'No se pudo eliminar el dispenser.');
    } finally {
      setBusyDispenserId(null);
    }
  };

  const resetAddForm = () => {
    setNewDispenserName('');
    setNewDispenserCapacity('');
    setNewDispenserErrors({});
    setAddingDispenser(false);
  };

  const handleDeleteGroup = async () => {
    try {
      setDeleting(true);
      await softDeleteFeedingGroup(groupId);
      toast.success(`Grupo "${group.name}" eliminado.`);
      navigate(ROUTES.INVENTORY, { replace: true });
    } catch (err) {
      // Caso esperado: si hay pets activas en el grupo, el service rechaza
      // con GROUP_HAS_ACTIVE_PETS. Mostramos el mensaje literal.
      toast.error(err.message || 'No se pudo eliminar el grupo.');
      setDeleting(false);
    }
  };

  const handleAddDispenser = async (e) => {
    e?.preventDefault();
    const errs = {};
    if (!newDispenserName.trim()) errs.name = 'Requerido';
    const capNum = Number(newDispenserCapacity);
    if (!newDispenserCapacity || !Number.isInteger(capNum)) {
      errs.capacityGr = 'Inválido';
    } else if (
      capNum < VALIDATION.DISPENSER_CAPACITY_MIN_GR ||
      capNum > VALIDATION.DISPENSER_CAPACITY_MAX_GR
    ) {
      errs.capacityGr = `Entre ${VALIDATION.DISPENSER_CAPACITY_MIN_GR} y ${VALIDATION.DISPENSER_CAPACITY_MAX_GR} g`;
    }
    if (Object.keys(errs).length > 0) {
      setNewDispenserErrors(errs);
      return;
    }

    try {
      setAddingBusy(true);
      await addDispenser(groupId, {
        name: newDispenserName.trim(),
        capacityGr: capNum,
        stockGr: 0,
      });
      toast.success(`${newDispenserName.trim()} agregado.`);
      resetAddForm();
    } catch (err) {
      toast.error(err.message || 'No se pudo agregar el dispenser.');
    } finally {
      setAddingBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Button
        variant="ghost"
        size="sm"
        leftIcon={ArrowLeft}
        onClick={() => navigate(-1)}
        className="self-start"
      >
        Volver
      </Button>

      <div>
        <h1 className="text-2xl font-bold text-slate-900">Editar grupo</h1>
        <p className="text-sm text-slate-500">Cambios al grupo {group.name}.</p>
      </div>

      <form onSubmit={handleSubmitGroup} className="flex flex-col gap-4">
        <Card>
          <div className="flex items-start gap-3 mb-4">
            <div className="rounded-lg bg-brand-100 p-2.5">
              <Layers className="h-5 w-5 text-brand-700" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-slate-900">Datos del grupo</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                El stock actual no se edita acá — usá "Compré bolsas", "Abrir bolsa"
                o "Ajustar stock".
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Input
              label="Nombre del grupo"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={errors.name}
              maxLength={40}
            />

            <Input
              label="Tamaño de cada bolsa cerrada (gramos)"
              type="number"
              inputMode="numeric"
              value={bagCapacityGr}
              onChange={(e) => setBagCapacityGr(e.target.value)}
              error={errors.bagCapacityGr}
              placeholder="Ej: 15000"
              hint={
                bagCapacityLocked
                  ? 'Para cambiar el tamaño primero abrí las bolsas que tenés en reserva'
                  : 'Tamaño de las bolsas cerradas que comprás. Opcional.'
              }
              disabled={bagCapacityLocked}
              min={VALIDATION.BAG_CAPACITY_MIN_GR}
              max={VALIDATION.BAG_CAPACITY_MAX_GR}
            />

            <Input
              label="Avisar cuando queden menos de X días"
              type="number"
              inputMode="numeric"
              value={lowStockThresholdDays}
              onChange={(e) => setLowStockThresholdDays(e.target.value)}
              error={errors.lowStockThresholdDays}
              placeholder={`Default: ${INVENTORY.DEFAULT_LOW_STOCK_THRESHOLD_DAYS}`}
              hint={`Umbral para mostrar "Comprar pronto" en este grupo. Entre ${VALIDATION.LOW_STOCK_THRESHOLD_DAYS_MIN} y ${VALIDATION.LOW_STOCK_THRESHOLD_DAYS_MAX}. Dejá vacío para usar el default (${INVENTORY.DEFAULT_LOW_STOCK_THRESHOLD_DAYS}).`}
              min={VALIDATION.LOW_STOCK_THRESHOLD_DAYS_MIN}
              max={VALIDATION.LOW_STOCK_THRESHOLD_DAYS_MAX}
            />
          </div>
        </Card>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            fullWidth
            onClick={() => navigate(ROUTES.INVENTORY)}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button type="submit" variant="primary" fullWidth isLoading={submitting}>
            Guardar cambios
          </Button>
        </div>
      </form>

      <Card>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-semibold text-slate-900">Dispensers</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {dispensers.length === 0
                ? 'Sin dispensers: la mascota come directo de la bolsa principal.'
                : `${dispensers.length} configurado${dispensers.length === 1 ? '' : 's'}.`}
            </p>
          </div>
          {!addingDispenser && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leftIcon={Plus}
              onClick={() => setAddingDispenser(true)}
              disabled={!canAddMore}
            >
              Agregar
            </Button>
          )}
        </div>

        {!canAddMore && !addingDispenser && (
          <div className="flex items-start gap-2.5 rounded-lg bg-amber-50 px-3 py-2.5 ring-1 ring-amber-100 mb-3">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
            <p className="text-xs text-amber-900">
              Llegaste al máximo de {VALIDATION.MAX_DISPENSERS_PER_GROUP} dispensers por grupo.
            </p>
          </div>
        )}

        {addingDispenser && (
          <div className="rounded-xl border border-brand-300 bg-brand-50/40 p-3 mb-3 flex flex-col gap-3">
            <p className="text-sm font-medium text-slate-900">Nuevo dispenser</p>
            <Input
              label="Nombre"
              placeholder="Ej: Cocina, Patio..."
              value={newDispenserName}
              onChange={(e) => setNewDispenserName(e.target.value)}
              error={newDispenserErrors.name}
              maxLength={VALIDATION.DISPENSER_NAME_MAX}
              autoFocus
            />
            <Input
              label="Capacidad (gramos)"
              type="number"
              inputMode="numeric"
              placeholder="Ej: 1000"
              value={newDispenserCapacity}
              onChange={(e) => setNewDispenserCapacity(e.target.value)}
              error={newDispenserErrors.capacityGr}
              hint="Arranca vacío. Después rellenalo desde la bolsa."
              min={VALIDATION.DISPENSER_CAPACITY_MIN_GR}
              max={VALIDATION.DISPENSER_CAPACITY_MAX_GR}
            />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={resetAddForm}
                disabled={addingBusy}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="primary"
                fullWidth
                onClick={handleAddDispenser}
                isLoading={addingBusy}
              >
                Agregar
              </Button>
            </div>
          </div>
        )}

        {dispensers.length === 0 && !addingDispenser && (
          <p className="text-sm text-slate-500 py-4 text-center">
            Tocá "Agregar" para sumar un dispenser.
          </p>
        )}

        {dispensers.length > 0 && (
          <ul className="flex flex-col gap-2">
            {dispensers.map((d) => {
              const editState = editing[d.id];
              const isBusy = busyDispenserId === d.id;

              return (
                <li
                  key={d.id}
                  className="rounded-xl border border-slate-200 p-3 flex flex-col gap-2"
                >
                  <div className="flex items-center gap-2">
                    {editState?.mode === 'name' ? (
                      <>
                        <Input
                          value={editState.value}
                          onChange={(e) =>
                            setEditing({
                              [d.id]: { mode: 'name', value: e.target.value },
                            })
                          }
                          maxLength={VALIDATION.DISPENSER_NAME_MAX}
                          autoFocus
                          className="flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => commitEditName(d)}
                          disabled={isBusy}
                          aria-label="Guardar nombre"
                          className="text-status-green hover:opacity-70 disabled:opacity-40"
                        >
                          <Check className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={isBusy}
                          aria-label="Cancelar"
                          className="text-slate-400 hover:text-slate-700 disabled:opacity-40"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="flex-1 font-medium text-slate-900 truncate">{d.name}</p>
                        <button
                          type="button"
                          onClick={() => startEditName(d)}
                          disabled={isBusy || Boolean(editState)}
                          aria-label={`Renombrar ${d.name}`}
                          className="text-slate-400 hover:text-brand-700 disabled:opacity-40"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemove(d)}
                          disabled={isBusy || Boolean(editState)}
                          aria-label={`Quitar ${d.name}`}
                          className="text-slate-400 hover:text-status-red disabled:opacity-40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500">Stock:</span>
                    <span className="font-medium text-slate-900">
                      {formatGrams(d.stockGr ?? 0)}
                    </span>
                    <span className="text-slate-300">·</span>
                    <span className="text-slate-500">Capacidad:</span>
                    {editState?.mode === 'size' ? (
                      <div className="flex items-center gap-1 flex-1">
                        <input
                          type="number"
                          inputMode="numeric"
                          value={editState.value}
                          onChange={(e) =>
                            setEditing({
                              [d.id]: { mode: 'size', value: e.target.value },
                            })
                          }
                          min={VALIDATION.DISPENSER_CAPACITY_MIN_GR}
                          max={VALIDATION.DISPENSER_CAPACITY_MAX_GR}
                          className="flex-1 max-w-[100px] rounded-lg border border-slate-300 px-2 py-1 text-sm"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => commitEditSize(d)}
                          disabled={isBusy}
                          aria-label="Guardar capacidad"
                          className="text-status-green hover:opacity-70 disabled:opacity-40"
                        >
                          <Check className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={isBusy}
                          aria-label="Cancelar"
                          className="text-slate-400 hover:text-slate-700 disabled:opacity-40"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="font-medium text-slate-900">
                          {formatGrams(d.capacityGr)}
                        </span>
                        <button
                          type="button"
                          onClick={() => startEditSize(d)}
                          disabled={isBusy || Boolean(editState)}
                          aria-label={`Cambiar capacidad de ${d.name}`}
                          className="text-slate-400 hover:text-brand-700 disabled:opacity-40 ml-auto"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* Zona peligrosa: soft delete del grupo. El service rechaza si quedan
          pets activas (GROUP_HAS_ACTIVE_PETS) — hay que moverlas a otro
          grupo o eliminarlas primero. */}
      <Card className="ring-1 ring-red-100 bg-red-50/40">
        <h3 className="font-semibold text-status-red mb-1">Zona peligrosa</h3>
        <p className="text-xs text-slate-600 mb-3">
          Eliminar el grupo "{group.name}" lo saca del inventario.
          {petsInGroup.length > 0 ? (
            <>
              {' '}Antes de eliminar, mové las{' '}
              <strong>
                {petsInGroup.length} mascota{petsInGroup.length === 1 ? '' : 's'}
              </strong>{' '}
              que lo usan a otro grupo (sino el servicio va a rechazar la operación).
            </>
          ) : (
            ' La bitácora histórica de eventos del grupo se preserva.'
          )}
        </p>
        <Button
          type="button"
          variant="danger"
          leftIcon={Trash2}
          onClick={() => setConfirmDelete(true)}
          disabled={petsInGroup.length > 0}
        >
          Eliminar grupo
        </Button>
      </Card>

      <Modal
        isOpen={confirmDelete}
        onClose={() => !deleting && setConfirmDelete(false)}
        title={`¿Eliminar "${group.name}"?`}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 px-3 py-2.5 ring-1 ring-amber-100">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-amber-900">
              El grupo deja de aparecer en el inventario. La bitácora de
              eventos del grupo (refills, bag_opened, etc.) se mantiene como
              registro histórico.
            </p>
          </div>
          <p className="text-xs text-slate-500">
            No se puede deshacer desde la app.
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              fullWidth
              leftIcon={Trash2}
              isLoading={deleting}
              onClick={handleDeleteGroup}
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EditGroupPage;
